/**
 * The routes, behind the sign-in gate (design 10). Until the session is known, only sign-in, an invitation link,
 * About, the Method page and Privacy are shown; every other address shows sign-in in place, so the address asked for opens after
 * signing in. A screen whose phase has not come yet is still a route: it says which phase brings it.
 *
 * PROVENANCE: the gate of C:\Gap-Assessor\src\App.tsx @ 2023d32 (a 401 returns to sign-in; the session is looked
 * at again on window focus). Pages are lazy-loaded.
 */

import { lazy, Suspense, useEffect, useRef } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { Shell } from './components/Shell'
import { Button, Loading } from './components/ui'
import { useLanguage } from './i18n/LanguageProvider'
import { formatInstantDay } from './lib/dates'
import { SessionProvider, useSession, useSessionState } from './lib/session'
import { allItems, isBuilt } from './nav/nav-config'

const SignInPage = lazy(() => import('./pages/auth/SignInPage').then((m) => ({ default: m.SignInPage })))
const InvitePage = lazy(() => import('./pages/auth/InvitePage').then((m) => ({ default: m.InvitePage })))
const StaticPages = () => import('./pages/StaticPages')
const AboutPage = lazy(() => StaticPages().then((m) => ({ default: m.AboutPage })))
const PrivacyPage = lazy(() => StaticPages().then((m) => ({ default: m.PrivacyPage })))
const NotFoundPage = lazy(() => StaticPages().then((m) => ({ default: m.NotFoundPage })))
const LaterPage = lazy(() => StaticPages().then((m) => ({ default: m.LaterPage })))
const MethodPage = lazy(() => import('./pages/MethodPage').then((m) => ({ default: m.MethodPage })))
const Portfolio = () => import('./pages/portfolio/PortfolioHome')
const PortfolioHome = lazy(() => Portfolio().then((m) => ({ default: m.PortfolioHome })))
const MyWorkPage = lazy(() => Portfolio().then((m) => ({ default: m.MyWorkPage })))
const InboxPage = lazy(() => import('./pages/portfolio/InboxPage').then((m) => ({ default: m.InboxPage })))
const PortfolioHeatPage = lazy(() => import('./pages/portfolio/PortfolioHeatPage').then((m) => ({ default: m.PortfolioHeatPage })))
const ProgressPage = lazy(() => import('./pages/client/ProgressPage').then((m) => ({ default: m.ProgressPage })))
const AiPages = () => import('./pages/client/AiPages')
const AiListPage = lazy(() => AiPages().then((m) => ({ default: m.AiListPage })))
const AiCallPage = lazy(() => AiPages().then((m) => ({ default: m.AiCallPage })))
const Modules = () => import('./pages/client/ModulePages')
const ModulesPage = lazy(() => Modules().then((m) => ({ default: m.ModulesPage })))
const ModulePage = lazy(() => Modules().then((m) => ({ default: m.ModulePage })))
const ModuleRunPage = lazy(() => Modules().then((m) => ({ default: m.ModuleRunPage })))
const Handoffs = () => import('./pages/client/HandoffPages')
const NextStepsPage = lazy(() => Handoffs().then((m) => ({ default: m.NextStepsPage })))
const HandoffsPage = lazy(() => Handoffs().then((m) => ({ default: m.HandoffsPage })))
const HandoffPage = lazy(() => Handoffs().then((m) => ({ default: m.HandoffPage })))
const PortfolioCycle = lazy(() => import('./pages/portfolio/PortfolioCycle').then((m) => ({ default: m.PortfolioCycle })))
const ClientLayout = lazy(() => import('./pages/client/context').then((m) => ({ default: m.ClientLayout })))
const Client = () => import('./pages/client/ClientPages')
const ClientHome = lazy(() => Client().then((m) => ({ default: m.ClientHome })))
const MembersPage = lazy(() => Client().then((m) => ({ default: m.MembersPage })))
const AuditPage = lazy(() => Client().then((m) => ({ default: m.AuditPage })))
const Homes = () => import('./pages/client/ClientHomes')
const WorkHome = lazy(() => Homes().then((m) => ({ default: m.WorkHome })))
const Profile = () => import('./pages/client/ProfilePages')
const ProfilePage = lazy(() => Profile().then((m) => ({ default: m.ProfilePage })))
const ServicesPage = lazy(() => Profile().then((m) => ({ default: m.ServicesPage })))
const ServicePage = lazy(() => Profile().then((m) => ({ default: m.ServicePage })))
const Cycle = () => import('./pages/client/CyclePages')
const ClientCycle = lazy(() => Cycle().then((m) => ({ default: m.ClientCycle })))
const OccurrencePage = lazy(() => Cycle().then((m) => ({ default: m.OccurrencePage })))
const SeriesPage = lazy(() => Cycle().then((m) => ({ default: m.SeriesPage })))
const NewSeriesPage = lazy(() => Cycle().then((m) => ({ default: m.NewSeriesPage })))
const TemplatesPage = lazy(() => Cycle().then((m) => ({ default: m.TemplatesPage })))
const Actions = () => import('./pages/client/ActionsPages')
const ActionsPage = lazy(() => Actions().then((m) => ({ default: m.ActionsPage })))
const ActionPage = lazy(() => Actions().then((m) => ({ default: m.ActionPage })))
const Documents = () => import('./pages/client/DocumentsPages')
const DocumentsPage = lazy(() => Documents().then((m) => ({ default: m.DocumentsPage })))
const DocumentPage = lazy(() => Documents().then((m) => ({ default: m.DocumentPage })))
const CustomerDataPage = lazy(() => import('./pages/client/CustomerDataPage').then((m) => ({ default: m.CustomerDataPage })))
const Assessment = () => import('./pages/client/AssessmentPages')
const AssessmentsPage = lazy(() => Assessment().then((m) => ({ default: m.AssessmentsPage })))
const AssessmentPage = lazy(() => Assessment().then((m) => ({ default: m.AssessmentPage })))
const RequestPage = lazy(() => Assessment().then((m) => ({ default: m.RequestPage })))
const InquiryPage = lazy(() => Assessment().then((m) => ({ default: m.InquiryPage })))
const Engine = () => import('./pages/client/EnginePages')
const AnalysisPage = lazy(() => Engine().then((m) => ({ default: m.AnalysisPage })))
const EvidenceInboxPage = lazy(() => import('./pages/client/ProposalParts').then((m) => ({ default: m.InboxPage })))
const QuestionnairesPage = lazy(() => import('./pages/client/QuestionnairePages').then((m) => ({ default: m.QuestionnairesPage })))
const PracticePage = lazy(() => Engine().then((m) => ({ default: m.PracticePage })))
const EngineRecords = () => import('./pages/client/EngineRecords')
const EvidencePage = lazy(() => EngineRecords().then((m) => ({ default: m.EvidencePage })))
const TestsPage = lazy(() => EngineRecords().then((m) => ({ default: m.TestsPage })))
const ReportPage = lazy(() => import('./pages/client/ReportPage').then((m) => ({ default: m.ReportPage })))
const HandoverPage = lazy(() => import('./pages/client/HandoverPages').then((m) => ({ default: m.HandoverPage })))
const RoadmapPage = lazy(() => import('./pages/client/HandoverPages').then((m) => ({ default: m.RoadmapPage })))
const PlanPage = lazy(() => import('./pages/client/PlanPage').then((m) => ({ default: m.PlanPage })))
const PacksPage = lazy(() => import('./pages/client/PacksPage').then((m) => ({ default: m.PacksPage })))
const BusinessPage = lazy(() => import('./pages/client/BusinessPage').then((m) => ({ default: m.BusinessPage })))
const MandatePage = lazy(() => import('./pages/client/MandatePage').then((m) => ({ default: m.MandatePage })))
const Discover = () => import('./pages/client/DiscoverPages')
const DiscoverPage = lazy(() => Discover().then((m) => ({ default: m.DiscoverPage })))
const MissingPage = lazy(() => Discover().then((m) => ({ default: m.MissingPage })))
const Heat = () => import('./pages/client/HeatPages')
const HeatMapPage = lazy(() => Heat().then((m) => ({ default: m.HeatMapPage })))
const RolePage = lazy(() => Heat().then((m) => ({ default: m.RolePage })))
const PicturePage = lazy(() => Discover().then((m) => ({ default: m.PicturePage })))
const Regulation = () => import('./pages/client/RegulationPages')
const RegulationPage = lazy(() => Regulation().then((m) => ({ default: m.RegulationPage })))
const CalendarPage = lazy(() => Regulation().then((m) => ({ default: m.CalendarPage })))
const AffectsPage = lazy(() => import('./pages/client/AffectsPage').then((m) => ({ default: m.AffectsPage })))
const OnboardingPage = lazy(() => import('./pages/client/OnboardingPage').then((m) => ({ default: m.OnboardingPage })))
const Firm = () => import('./pages/firm/FirmPages')
const PeoplePage = lazy(() => Firm().then((m) => ({ default: m.PeoplePage })))
const ClientsPage = lazy(() => Firm().then((m) => ({ default: m.ClientsPage })))
const NewClientPage = lazy(() => Firm().then((m) => ({ default: m.NewClientPage })))
const SettingsPage = lazy(() => Firm().then((m) => ({ default: m.SettingsPage })))
const AiSpendPage = lazy(() => import('./pages/firm/AiSpendPage').then((m) => ({ default: m.AiSpendPage })))
const Library = () => import('./pages/firm/LibraryPage')
const LibraryPage = lazy(() => Library().then((m) => ({ default: m.LibraryPage })))
const TemplatePage = lazy(() => Library().then((m) => ({ default: m.TemplatePage })))

/** The route patterns of the built screens (the not-yet screens come from nav-config). Tested in nav.test.ts. */
export const BUILT_ROUTES = [
  '/portfolio',
  '/portfolio/work',
  '/portfolio/inbox',
  '/portfolio/heat',
  '/portfolio/cycle',
  '/firm/people',
  '/firm/clients',
  '/firm/clients/new',
  '/firm/library',
  '/firm/library/templates/:id',
  '/firm/settings',
  '/firm/ai-spend',
  '/about',
  '/about/method',
  '/privacy',
  '/invite',
  '/c/:client',
  '/c/:client/work',
  '/c/:client/work/happened',
  '/c/:client/work/deadlines',
  '/c/:client/work/todo',
  '/c/:client/services',
  '/c/:client/services/:service',
  '/c/:client/mandate',
  '/c/:client/business',
  '/c/:client/discover',
  '/c/:client/progress',
  '/c/:client/ai',
  '/c/:client/ai/:call',
  '/c/:client/modules',
  '/c/:client/modules/:area/:module',
  '/c/:client/module-runs/:run',
  '/c/:client/handoffs',
  '/c/:client/handoffs/:handoff',
  '/c/:client/picture',
  '/c/:client/assessments',
  '/c/:client/assessments/:assessment',
  '/c/:client/assessments/:assessment/analysis',
  '/c/:client/assessments/:assessment/inbox',
  '/c/:client/assessments/:assessment/questionnaires',
  '/c/:client/assessments/:assessment/missing',
  '/c/:client/assessments/:assessment/heatmap',
  '/c/:client/assessments/:assessment/role',
  '/c/:client/assessments/:assessment/regulation',
  '/c/:client/assessments/:assessment/calendar',
  '/c/:client/assessments/:assessment/affects',
  '/c/:client/assessments/:assessment/evidence',
  '/c/:client/assessments/:assessment/tests',
  '/c/:client/assessments/:assessment/report',
  '/c/:client/assessments/:assessment/handover',
  '/c/:client/assessments/:assessment/roadmap',
  '/c/:client/assessments/:assessment/mitigation',
  '/c/:client/assessments/:assessment/packs',
  '/c/:client/assessments/:assessment/practices/:practice',
  '/c/:client/assessments/:assessment/gaps/:practice/next',
  '/c/:client/requests/:request',
  '/c/:client/inquiries/:inquiry',
  '/c/:client/cycle',
  '/c/:client/cycle/templates',
  '/c/:client/cycle/series/new',
  '/c/:client/cycle/series/:series',
  '/c/:client/cycle/:occ',
  '/c/:client/registers/actions',
  '/c/:client/registers/actions/:action',
  '/c/:client/documents',
  '/c/:client/documents/:doc',
  '/c/:client/profile',
  '/c/:client/customer-data',
  '/c/:client/onboarding',
  '/c/:client/members',
  '/c/:client/audit',
] as const

/**
 * The notice that a password was reset, shown until it is acknowledged. It is modal: it takes the focus when it
 * opens and Tab stays on its button, so a keyboard user is not left behind it in the page it covers.
 */
export function ResetNotice() {
  const { t, language } = useLanguage()
  const { resetNotice, acknowledgeReset } = useSession()
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!resetNotice) return
    const focusable = () => Array.from(box.current?.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea') ?? [])
    focusable()[0]?.focus()
    const keep = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const all = focusable()
      if (all.length === 0) return
      e.preventDefault()
      const at = all.indexOf(document.activeElement as HTMLElement)
      const next = at === -1 ? 0 : (at + (e.shiftKey ? all.length - 1 : 1)) % all.length
      all[next]?.focus()
    }
    document.addEventListener('keydown', keep)
    return () => document.removeEventListener('keydown', keep)
  }, [resetNotice])
  if (!resetNotice) return null
  const date = formatInstantDay(resetNotice.at, language)
  return (
    <div ref={box} role="alertdialog" aria-modal="true" aria-labelledby="reset-title" className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-4">
      <div className="max-w-md space-y-3 rounded-lg bg-white p-6 shadow-xl">
        <h2 id="reset-title" className="text-lg font-semibold">
          {t('resetNotice.title')}
        </h2>
        <p>
          {resetNotice.via === 'host_command'
            ? t('resetNotice.host', { date })
            : t('resetNotice.body', { date, requester: resetNotice.requestedBy ?? '', confirmer: resetNotice.confirmedBy ?? '' })}
        </p>
        <Button onClick={() => void acknowledgeReset()}>{t('resetNotice.ack')}</Button>
      </div>
    </div>
  )
}

function clientRoute(path: string): string {
  return path.replace(/^\/c\/:client\/?/, '')
}

/** A client's pages, below /c/:client (the client segment is resolved once, by ClientLayout). */
export function ClientRoutes() {
  const later = allItems().filter((i) => !isBuilt(i) && i.to.startsWith('/c/'))
  return (
    <ClientLayout>
      <Routes>
        <Route index element={<ClientHome />} />
        <Route path="work" element={<WorkHome />} />
        <Route path="work/happened" element={<WorkHome focus="happened" />} />
        <Route path="work/deadlines" element={<WorkHome focus="deadlines" />} />
        <Route path="work/todo" element={<WorkHome focus="todo" />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="services/:service" element={<ServicePage />} />
        <Route path="mandate" element={<MandatePage />} />
        <Route path="business" element={<BusinessPage />} />
        <Route path="discover" element={<DiscoverPage />} />
        <Route path="progress" element={<ProgressPage />} />
        <Route path="ai" element={<AiListPage />} />
        <Route path="ai/:call" element={<AiCallPage />} />
        <Route path="modules" element={<ModulesPage />} />
        <Route path="modules/:area/:module" element={<ModulePage />} />
        <Route path="module-runs/:run" element={<ModuleRunPage />} />
        <Route path="handoffs" element={<HandoffsPage />} />
        <Route path="handoffs/:handoff" element={<HandoffPage />} />
        <Route path="picture" element={<PicturePage />} />
        <Route path="assessments" element={<AssessmentsPage />} />
        <Route path="assessments/:assessment" element={<AssessmentPage />} />
        <Route path="assessments/:assessment/analysis" element={<AnalysisPage />} />
        <Route path="assessments/:assessment/inbox" element={<EvidenceInboxPage />} />
        <Route path="assessments/:assessment/questionnaires" element={<QuestionnairesPage />} />
        <Route path="assessments/:assessment/missing" element={<MissingPage />} />
        <Route path="assessments/:assessment/heatmap" element={<HeatMapPage />} />
        <Route path="assessments/:assessment/role" element={<RolePage />} />
        <Route path="assessments/:assessment/regulation" element={<RegulationPage />} />
        <Route path="assessments/:assessment/calendar" element={<CalendarPage />} />
        <Route path="assessments/:assessment/affects" element={<AffectsPage />} />
        <Route path="assessments/:assessment/evidence" element={<EvidencePage />} />
        <Route path="assessments/:assessment/tests" element={<TestsPage />} />
        <Route path="assessments/:assessment/report" element={<ReportPage />} />
        <Route path="assessments/:assessment/handover" element={<HandoverPage />} />
        <Route path="assessments/:assessment/roadmap" element={<RoadmapPage />} />
        <Route path="assessments/:assessment/mitigation" element={<PlanPage />} />
        <Route path="assessments/:assessment/packs" element={<PacksPage />} />
        <Route path="assessments/:assessment/practices/:practice" element={<PracticePage />} />
        <Route path="assessments/:assessment/gaps/:practice/next" element={<NextStepsPage />} />
        <Route path="requests/:request" element={<RequestPage />} />
        <Route path="inquiries/:inquiry" element={<InquiryPage />} />
        <Route path="cycle" element={<ClientCycle />} />
        <Route path="cycle/templates" element={<TemplatesPage />} />
        <Route path="cycle/series/new" element={<NewSeriesPage />} />
        <Route path="cycle/series/:series" element={<SeriesPage />} />
        <Route path="cycle/:occ" element={<OccurrencePage />} />
        <Route path="registers/actions" element={<ActionsPage />} />
        <Route path="registers/actions/:action" element={<ActionPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="documents/:doc" element={<DocumentPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="customer-data" element={<CustomerDataPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="members" element={<MembersPage />} />
        <Route path="audit" element={<AuditPage />} />
        {later.map((i) => (
          <Route key={i.id} path={clientRoute(i.to)} element={<LaterPage titleKey={i.labelKey} answersKey={i.answersKey} />} />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ClientLayout>
  )
}

function SignedIn() {
  const later = allItems().filter((i) => !isBuilt(i) && !i.to.startsWith('/c/'))
  return (
    <Shell>
      <ResetNotice />
      <Suspense fallback={<div className="p-6"><Loading /></div>}>
        <Routes>
          <Route path="/" element={<Navigate to="/portfolio" replace />} />
          <Route path="/portfolio" element={<PortfolioHome />} />
          <Route path="/portfolio/work" element={<MyWorkPage />} />
          <Route path="/portfolio/inbox" element={<InboxPage />} />
          <Route path="/portfolio/heat" element={<PortfolioHeatPage />} />
          <Route path="/portfolio/cycle" element={<PortfolioCycle />} />
          <Route path="/firm/people" element={<PeoplePage />} />
          <Route path="/firm/clients" element={<ClientsPage />} />
          <Route path="/firm/clients/new" element={<NewClientPage />} />
          <Route path="/firm/library" element={<LibraryPage />} />
          <Route path="/firm/library/templates/:id" element={<TemplatePage />} />
          <Route path="/firm/settings" element={<SettingsPage />} />
          <Route path="/firm/ai-spend" element={<AiSpendPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about/method" element={<MethodPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/invite" element={<Navigate to="/portfolio" replace />} />
          {later.map((i) => (
            <Route key={i.id} path={i.to} element={<LaterPage titleKey={i.labelKey} answersKey={i.answersKey} />} />
          ))}
          <Route path="/c/:client/*" element={<ClientRoutes />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Shell>
  )
}

export default function App() {
  const { t } = useLanguage()
  const { value, checked, unreachable, refresh } = useSessionState()

  if (!checked) return <div className="p-8"><Loading /></div>

  if (!value) {
    return (
      <div className="min-h-full">
        {unreachable && (
          <div className="border-b border-ol-red/30 bg-ol-red-soft px-6 py-2 text-ol-red" role="alert">
            {t('common.unreachable')}
          </div>
        )}
        <Suspense fallback={<div className="p-8"><Loading /></div>}>
          <Routes>
            <Route path="/about" element={<AboutPage />} />
            <Route path="/about/method" element={<MethodPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/invite" element={<InvitePage onSignedIn={() => void refresh()} />} />
            <Route path="*" element={<SignInPage onSignedIn={() => void refresh()} />} />
          </Routes>
        </Suspense>
      </div>
    )
  }

  return (
    <SessionProvider value={value}>
      <SignedIn />
    </SessionProvider>
  )
}
